import React from "react";
import { NavLink, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Music, Calendar, MapPin, CalendarDays, Megaphone, Link2, ListChecks, Users, Inbox, UserSquare2, ArrowLeft, Send, ShieldCheck, GitCompare, ScrollText } from "lucide-react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/dances", label: "Dances", icon: Music },
  { to: "/admin/assignments", label: "Dance Assignments", icon: Link2 },
  { to: "/admin/sessions", label: "Sessions & Playlists", icon: ListChecks },
  { to: "/admin/classes", label: "Classes", icon: Calendar, adminOnly: true },
  { to: "/admin/instructors", label: "Instructors", icon: UserSquare2, adminOnly: true },
  { to: "/admin/venues", label: "Venues", icon: MapPin, adminOnly: true },
  { to: "/admin/events", label: "Events", icon: CalendarDays, adminOnly: true },
  { to: "/admin/submissions", label: "Event Submissions", icon: Inbox, adminOnly: true },
  { to: "/admin/announcements", label: "Announcements", icon: Megaphone, perm: "announce_manage" },
  { to: "/admin/users", label: "Members", icon: Users, adminOnly: true },
  { to: "/admin/safety", label: "Member Safety", icon: ShieldCheck, perm: "safety_access" },
  { to: "/admin/sms", label: "Emergency SMS", icon: Send, perm: "sms_send" },
  { to: "/admin/reconciliation", label: "SMS Reconciliation", icon: GitCompare, perm: "sms_send" },
  { to: "/admin/permission-audit", label: "Permission Audit", icon: ScrollText, adminOnly: true },
];

function visible(u, n) {
  if (n.adminOnly) return u?.role === "admin";
  if (n.perm) return u?.role === "admin" || u?.[n.perm] === true;
  return true;
}

export function AdminLayout({ title, subtitle, actions, children }) {
  const { user } = useAuth();
  const items = NAV.filter((n) => visible(user, n));
  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-6 px-4 py-6 sm:px-6 lg:flex-row lg:px-8">
      <aside className="min-w-0 lg:w-60 lg:shrink-0">
        <Link to="/" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-[#6E5B51] hover:text-[#B04B26]"><ArrowLeft size={15} /> Back to site</Link>
        <nav className="flex gap-1 overflow-x-auto rounded-2xl border border-border bg-card p-2 lg:flex-col">
          {items.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "")}`}
              className={({ isActive }) => `flex shrink-0 items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold ${isActive ? "bg-[#C85A32] text-white" : "text-[#5C4A42] hover:bg-secondary"}`}>
              <Icon size={17} /> {label}
            </NavLink>
          ))}
        </nav>
      </aside>
      <main className="min-w-0 flex-1">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-extrabold text-[#2D1E18] sm:text-3xl">{title}</h1>
            {subtitle && <p className="mt-1 text-sm text-[#5C4A42]">{subtitle}</p>}
          </div>
          {actions}
        </div>
        {children}
      </main>
    </div>
  );
}

export const btn = "inline-flex items-center gap-1.5 rounded-full bg-[#C85A32] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#B04B26]";
export const btnGhost = "inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3.5 py-2 text-sm font-semibold text-[#5C4A42] hover:border-[#C85A32]";
export const inp = "w-full rounded-xl border border-border bg-white px-3 py-2.5 text-[#2D1E18] outline-none focus:border-[#C85A32]";
export const lbl = "mb-1 block text-sm font-semibold text-[#2D1E18]";
